import { computed, defineComponent, onMounted, ref, inject, watch  } from "vue";
import { BasketApi, CheckoutApi, MemberApi } from "@/services/Api";
import { ExtraInfo } from "@/models/Payment";
import { Checkout } from "@/models/Checkout";
import { Customer } from "@/models/Customer";
import { useAsyncSpinner } from "@/services/Spinner";
import BaseButton from "@/components/BaseButton.vue";
import Cart from "@/components/Cart.vue";
import CartSummary from "@/components/CartSummary.vue";
import SetZipCode from "@/components/SetZipCode.vue";
import KlarnaWidget from "@/components/KlarnaWidget.vue";
import AdditionalServices from "@/components/AdditionalServices.vue";
import CustomerDetails from "@/components/CustomerDetails.vue";
import CustomerDetailsEdit from "@/components/CustomerDetailsEdit.vue";
import BoxWithTitle from "@/components/BoxWithTitle.vue";
import BlurOverlay from "@/components/BlurOverlay.vue";
import InputGroup from "@/components/InputGroup.vue";
import Divider from "@/components/Divder.vue";
import LoginForm from "@/components/LoginForm.vue";
import CartLoginForm from "@/components/CartLoginForm.vue";
import FormControlTextArea from "@/components/FormControlTextArea.vue";
import AlternativeDelivery from "@/components/AlternativeDelivery.vue";
import UpdateModelHelper from "@/helpers/Helpers";
import { AxiosResponse } from "axios";
import { useI18n } from "vue-i18n";
import { InfoType } from "../../constants"

export default defineComponent({
  components: {
    Divider,
    InputGroup,
    CustomerDetails,
    CustomerDetailsEdit,
    BoxWithTitle,
    CartSummary,
    SetZipCode,
    AdditionalServices,
    Cart,
    BaseButton,
    LoginForm,
    CartLoginForm,
    BlurOverlay,
    KlarnaWidget,
    FormControlTextArea,
    AlternativeDelivery
  },
  data() {
    return{
      showPricesAsB2b:false,
      responseMessage:"",
    }
  },
  mounted() {
    this.showPricesAsB2b = inject<boolean>("show-prices-as-b2b", false);
  },
  props: {
    loggedIn: {
      type: Boolean,
      required: true,
    },
    mainurl: {
      type: String,
      required: false
    },
    registerurl: {
      type: String,
      required: false,
    },
    forgottenpasswordurl: {
      type: String,
      required: false
    },
    selectcompanyurl: {
      type: String,
      required: false
    },
    deliverytermspall: {
      type: String,
      required: false
    },
    deliverytermsbulk: {
      type: String,
      required: false
    }
  },
  methods: {
    async saveBasket() {
        const response = await MemberApi.saveBasket();
        this.responseMessage = response;
    },
  },
  setup(props) {
    const orderInformation = ref("");

    const checkout = ref<Checkout>();
    const agreeToTerms = ref<boolean>(false);
    const agreeToDeliveryTerms = ref<boolean>(false);
    const isBasketBulk = ref<boolean>(false);
    const isSubmitting = ref<boolean>(false);
    const initialCustomerId = ref<string>("");
    const { t } = useI18n();

    const responseMessageComment = ref<string | AxiosResponse>();
    const displayResponseMsg = ref<boolean>(false);
    const alternativeShipTo = ref<Customer | undefined>();
    const isBuyable = computed<boolean>(() =>
      checkout.value !== undefined &&
      checkout.value.basket.items.length > 0
    );
    const alternativeDelivery = ref<boolean>(false);
    const alternativeDeliverySaved = ref<boolean>(false);
    const shipToOrigin = ref<Customer>();
    const isResellerSuperUser = computed<boolean>(() => 
      checkout.value?.buyer.company?.companyType === "ÅF" && checkout.value?.buyer.company?.email.includes(checkout.value?.buyer.email) 
    );
    const isContractCustomer = computed<boolean>(() =>
      checkout.value?.payer.company?.companyType === "Avtal"
    );
    const customerComment = ref<string>();
    const companyCustomerComment = ref<string>();

    const hasMatchingZones = computed(() => {
      if (!checkout.value || !checkout.value.basket.items || !checkout.value.deliveryLimitations || checkout.value.deliveryLimitations.length === 0) {
        return false;
      }
      
      const deliveryLimitationCodes = checkout.value.deliveryLimitations.map(item => item.code).filter(Boolean);
      
      return checkout.value.basket.items.some(item => {
        const zones = item.zonesNotAllowed ? item.zonesNotAllowed.split(',').map(zone => zone.trim()) : [];
        return zones.some(zone => deliveryLimitationCodes.includes(zone));
      });
    });

    const hasDeliveryMethods = computed(() => {
      if (checkout.value?.deliveryMethods.length === 0) {
        return false;
      }
      return true;
    });

    const hasAddresses = computed(() => {
      return (checkout.value?.payer.invoiceAddress && checkout.value?.payer.invoiceAddress.zip != null) ||
      (checkout.value?.payer.deliveryAddresses && checkout.value?.payer.deliveryAddresses.length > 0) ||
      (checkout.value?.shipTo.deliveryAddresses && checkout.value?.shipTo.deliveryAddresses.length > 0);
    });

    const matchingProducts = computed(() => {
      if (!checkout.value || !checkout.value.basket.items || !checkout.value.deliveryLimitations) {
        return [];
      }
    
      const deliveryLimitationCodes = checkout.value.deliveryLimitations.map(item => item.code).filter(Boolean);
      
      return checkout.value.basket.items
        .filter(item => {
          const zones = item.zonesNotAllowed ? item.zonesNotAllowed.split(',').map(zone => zone.trim()) : [];
          return zones.some(zone => deliveryLimitationCodes.includes(zone));
        })
        .map(item => item.name);
    });

    const isKlarnaCheckout = computed<boolean>(() =>
      checkout.value !== undefined &&
      checkout.value.paymentResponse !== null &&
      checkout.value.paymentResponse.html !== null
    );

    const serviceItems = computed(() => {
      const allItems = checkout.value?.basket.items;

      if(allItems == null || allItems.length == 0)
        return [];

      return allItems.filter(item => item.productType === "Service");
    });

    const messages = ref<string[] | undefined>();
    const isLoading = ref<boolean>(false);
    const isZipSet = ref<boolean>(false);

    const onSubmit = async (): Promise<void> => {
      if (!isBuyable.value) {
        return;
      }
      if (alternativeDelivery.value && !alternativeDeliverySaved.value) {
        return;
      }
      await useAsyncSpinner(async () => {
        isSubmitting.value = true;
        
        if (checkout.value) {
          if(orderInformation.value) {
            await BasketApi.updateBasketComment(orderInformation.value);
          }

          const extraInfo: ExtraInfo[] = [];
          const order = await CheckoutApi.saveOrder(extraInfo, checkout.value.payer);
          messages.value = order.errorMessages;

          if (order.redirectUrl !== null && order.redirectUrl !== "") {
            location.href = order.redirectUrl;
          }
        }

        isSubmitting.value = false;
      });
    };

    const updateCheckout = async (): Promise<void> => {
      await useAsyncSpinner(async () => {
        isLoading.value = true;
        
        checkout.value = await CheckoutApi.getCheckout();
        isLoading.value = false;
      });
    };

    const updateZipCode = async (zip: string): Promise<void> => {
      await useAsyncSpinner(async () => {
        isLoading.value  = true;
        checkout.value = await CheckoutApi.setZipCode(zip);
        isLoading.value  = false;
        if (zip) {
          isZipSet.value = true;
        } else {
          isZipSet.value = false;
        }
      });
    };

    const toggleService = async (id: string): Promise<void> => {
      await useAsyncSpinner(async () => {
        isLoading.value = true;
        checkout.value = await CheckoutApi.toggleService(id);
        if(alternativeDelivery.value && alternativeShipTo.value) {
          await updateShipTo(alternativeShipTo.value);
        }
        isLoading.value = false;
      });
    };
    
    const updateBuyer = (customer: Customer): void => {
      if (checkout.value) {
        checkout.value.buyer = customer;
      }
    };

    const updateShipTo = async (customer: Customer): Promise<void> => {
      if (checkout.value) {
        await useAsyncSpinner(async () => {
          isLoading.value = true;
          checkout.value = await CheckoutApi.setShipTo(customer);
          alternativeShipTo.value = customer;
          isLoading.value = false;
        })
      }
    };

    const updatePayer = (customer: Customer): void => {
      if (checkout.value) {
        checkout.value.payer = customer;
      }
    };

    const updateDelivery = async (externalId: string): Promise<void> => {
      await useAsyncSpinner(async () => {
        checkout.value = await CheckoutApi.setDelivery(externalId);
      });
    };

    const updatePayment = async (externalId: string): Promise<void> => {
      await useAsyncSpinner(async () => {
        checkout.value = await CheckoutApi.setPayment(externalId);
      });
    };

    const updateAgreeToTerms = (): void => {
      agreeToTerms.value = !agreeToTerms.value;
    };

    const updateBasketComment = async (comment: string): Promise<void> => {
      await useAsyncSpinner(async () => {
        if (comment || comment === "") {
          isLoading.value = true;
          await BasketApi.updateBasketComment(comment);
          customerComment.value = comment;
          isLoading.value = false;
        }
      }) 
    };

    const saveComment = async() => {
      await useAsyncSpinner(async () => {
        if (alternativeShipTo.value && alternativeDelivery.value && alternativeDeliverySaved.value) {
          alternativeShipTo.value.orderInformation = orderInformation.value;
          const updateCompanyCustomerModel = UpdateModelHelper.createUpdateCompanyCustomerModel(alternativeShipTo.value);
          const model = await MemberApi.updateCompanyCustomer(updateCompanyCustomerModel);
          if(model.success) {
            responseMessageComment.value = t("Checkout.CommentUpdated");
          }
          showResponseMsg();
        }
        else if(isResellerSuperUser.value && checkout.value?.payer?.company?.companyType !== 'Avtal') {
          if(shipToOrigin.value) {
            shipToOrigin.value.orderInformation = orderInformation.value;
            const updateCompanyCustomerModel = UpdateModelHelper.createUpdateCompanyCustomerModel(shipToOrigin.value);
            updateCompanyCustomerModel.isSuperReseller = true;
            const model = await MemberApi.updateCompanyCustomer(updateCompanyCustomerModel);
            if(model.success) {
              responseMessageComment.value = t("Checkout.CommentUpdated");
            }
            showResponseMsg();
        }
        }
        else {
          if(checkout.value?.payer) {
            checkout.value.payer.orderInformation = orderInformation.value;
            const updateCustomerModel = UpdateModelHelper.createUpdateModel(checkout.value.payer);
            const model = await MemberApi.updateCustomer(updateCustomerModel);
            if(model.success) {
              responseMessageComment.value = t("Checkout.CommentUpdated");
            }
          showResponseMsg();
          }
        }
      });
    };

    const showResponseMsg = () => {
      displayResponseMsg.value = true;
      setTimeout(() => {
        displayResponseMsg.value = false;
      }, 7000);
    }

    const updateShipToComment = (comment): void => {
      if (comment || comment === "") {
        companyCustomerComment.value = comment;
      }
    };

    const updateAlternativeDelivery = (): void => {
      initialCustomerId.value = "";
        if (alternativeDelivery.value === false && shipToOrigin.value) {
          updateShipTo(shipToOrigin.value)
        }
    };

    const updateAlternativeDeliverySaved = (value): void => {
      alternativeDeliverySaved.value = value;
    };

    const updateComment = async(comment:string) => {
      orderInformation.value = comment;
      if(alternativeDelivery.value && alternativeShipTo.value) {
        alternativeShipTo.value.orderInformation = comment;
      }
      else if(props.loggedIn) {
        if(shipToOrigin.value) {
          shipToOrigin.value.orderInformation = comment
        }
        
      }
      await BasketApi.updateBasketComment(orderInformation.value);
    };

    //Run update comment after user stops typing
    const debounce = (func, wait) => {
      let timeout;
      return function executedFunction(...args) {
        const later = function () {
          timeout = null;
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    };

    const handleTyping = (() => {
      let timeout;
      
      return (event) => {
        orderInformation.value = event.target.value;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          debounce(updateComment, 500)(event.target.value);
        }, 500);
      };
    })();

    const watchorderInformation = () => {
      return watch(() => alternativeShipTo.value, (newDeliverTo) => {
        if (initialCustomerId.value == "") {
          orderInformation.value = newDeliverTo?.orderInformation ?? '';
        }
      });
    };

    const isBasketBulkOrder = (): boolean => {
      if (checkout?.value?.basket?.items) {
        for (const item of checkout?.value?.basket?.items) {
          if (item.unitType === "kg") {
            return true;
          }
        }
      }
      return false;
    }

    watchorderInformation();
    
    onMounted(async () => {
      await useAsyncSpinner(async () => {
        checkout.value = await CheckoutApi.getCheckout();
        isBasketBulk.value = isBasketBulkOrder();
        shipToOrigin.value = checkout.value.shipTo;

        const alternativeDeliveryExternalId = checkout.value.basket.info.find(x => x.code == InfoType.AlternativeDeliveryExteralId)?.value;
        const orderAgainFlag = checkout.value.basket.info.find(x => x.code == InfoType.OrderAgainFlag)?.value;

        if (alternativeDeliveryExternalId) {
          alternativeDelivery.value = true;
          initialCustomerId.value = alternativeDeliveryExternalId;
          await BasketApi.emptyAlternativeDelivery();
        }
        
        if (orderAgainFlag == "true" && checkout.value.basket.comment) {
          orderInformation.value = checkout.value.basket.comment;
          await BasketApi.removeOrderAgainFlag();
        } else {
          orderInformation.value = checkout.value.shipTo.orderInformation;
        }
          
          await BasketApi.updateBasketComment(orderInformation.value);
        });
      }
    );
    
    return {
      checkout,
      agreeToTerms,
      onSubmit,
      isSubmitting,
      isLoading,
      updateBuyer,
      updateShipTo,
      updatePayer,
      updateDelivery,
      updatePayment,
      updateCheckout,
      updateZipCode,
      toggleService,
      updateAgreeToTerms,
      isBuyable,
      isKlarnaCheckout,
      messages,
      hasMatchingZones,
      serviceItems,
      matchingProducts,
      hasDeliveryMethods,
      isZipSet,
      updateBasketComment,
      hasAddresses,
      alternativeDelivery,
      alternativeDeliverySaved,
      updateAlternativeDelivery,
      updateAlternativeDeliverySaved,
      isResellerSuperUser,
      isContractCustomer,
      updateShipToComment,
      updateComment,
      orderInformation,
      displayResponseMsg,
      responseMessageComment,
      saveComment,
      handleTyping,
      agreeToDeliveryTerms,
      isBasketBulk,
      initialCustomerId
    };
  },
});
